<script setup lang="ts">
import AnalysisNavigationBar from '@src/components/AnalysisNavigationBar.vue';
import ApplicationHeader from '@src/components/main-application-header/ApplicationHeader.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useServerStore } from '@/src/store/server';

import { useAppKeyboardShortcuts } from '../composables/useAppKeyboardShortcuts';
import { useAppState } from '../store/ocg-app-state';
import AlertBanner from './AlertBanner.vue';
import SegmentationDialog from './segmentation/SegmentationDialog.vue';
import StatusBar from './StatusBar.vue';

const appState = useAppState();
useAppKeyboardShortcuts();
const backgroundSrc = ref('');
const router = useRouter();
const route = useRoute();
const mainContainer = ref<HTMLElement | null>(null);

onMounted(() => {
  useServerStore().connect();
  backgroundSrc.value = `/images/${appState.timeOfDay.toLowerCase()}.jpg`;
  appState.setDefaultColorTheme();

  // Keep the tab alive
  if (navigator.locks) {
    navigator.locks.request('tab-keep-alive', () => {
      return new Promise(() => {});
    });
  }
  // keep the tab alive by updating the title every 30 seconds
  setInterval(() => {
    const { title } = document; // minimal activity
    document.title = title;
  }, 30000);
});

router.isReady().then(() => {
  watch(
    () => !!appState.session,
    (newValue) => {
      if (!newValue && (route.meta.requiresAuth ?? true)) {
        router.replace({ ...route, force: true });
      }
    },
    { immediate: true },
  );
});

const isLoginRoute = computed(
  () => router.currentRoute.value.path === '/login',
);
const showNavBar = computed(() =>
  ['/analysis'].includes(router.currentRoute.value.path),
);
</script>

<template>
  <div class="min-h-screen min-w-screen relative">
    <img alt="background" :src="backgroundSrc" class="background-image">
    <div
      class="flex min-h-screen min-w-screen"
      :class="{ 'glass-panel': !isLoginRoute }"
      style="border-radius: 0px"
    >
      <div class="mt-auto mb-auto pl-2">
        <AnalysisNavigationBar v-if="showNavBar" />
      </div>

      <div
        ref="mainContainer"
        class="flex-1 flex flex-col px-2 py-2 main-container"
      >
        <div class="prototype-banner">
          Prototype - Not for clinical use.
        </div>
        <ApplicationHeader class="flex-shrink-0" />
        <div class="view-container">
          <router-view />
        </div>
      </div>
    </div>

    <AlertBanner />
    <StatusBar />
    <SegmentationDialog />
  </div>
</template>

<style scoped>
.background-image {
  position: fixed;
  left: 0;
  top: 0;

  z-index: -100;
  width: 100%;
  height: 100%;
  object-fit: fill;

  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;

  filter: blur(3px);
  -webkit-filter: blur(3px);
  transform: scale(1.1);
}

.main-container {
  height: calc(100vh - 24px);
  overflow: hidden;
}

.view-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.prototype-banner {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  color: red;
  font-size: 0.7rem;
  z-index: 10000;
  pointer-events: none;
}
</style>
